import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Create,
  SimpleForm,
  ReferenceInput,
  AutocompleteInput,
  NumberInput,
  TextInput,
  DateInput,
  required,
  minValue,
} from "react-admin";
import type { CreateProps } from "react-admin";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { redirectToProjectModuleDashboard } from "@/containers/projects/redirect-to-project-module-dashboard";
import { useRouteId } from "@/utils/route-utility";

export const ProjectKarzanRepaymentCreate = (props: CreateProps) => {
  const [searchParams] = useSearchParams();
  const projectId = useRouteId();
  const initialKarzanId = useMemo(
    () => searchParams.get("projectKarzanId") ?? undefined,
    [searchParams]
  );
  const redirectTo = useMemo(
    () => redirectToProjectModuleDashboard({ routeProjectId: projectId, fallback: "list" }),
    [projectId]
  );

  if (!projectId) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">
          Open a project from the Projects list to record a repayment.
        </Typography>
      </Box>
    );
  }

  return (
    <Create
      {...props}
      redirect={redirectTo}
      transform={(data: Record<string, unknown>) => ({
        projectKarzanId: data.projectKarzanId,
        amount: Number(data.amount),
        repaymentDate: data.repaymentDate,
        remarks: typeof data.remarks === "string" ? data.remarks.trim() || undefined : data.remarks,
      })}
    >
      <SimpleForm defaultValues={{ projectKarzanId: initialKarzanId }}>
        <ReferenceInput
          source="projectKarzanId"
          reference="projectKarzans"
          filter={{ projectId }}
          perPage={100}
        >
          <AutocompleteInput
            optionText={(r: { lenderName?: string | null; principalAmount?: number | null }) =>
              `${r.lenderName ?? "Karzan"} — ₹${Number(r.principalAmount ?? 0).toLocaleString("en-IN")}`
            }
            label="Karzan"
            validate={required()}
          />
        </ReferenceInput>
        <NumberInput source="amount" validate={[required(), minValue(1)]} />
        <DateInput source="repaymentDate" validate={required()} />
        <TextInput source="remarks" multiline fullWidth />
      </SimpleForm>
    </Create>
  );
};
